<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: ControlWrapper                                        |
| Section: components                                              |
| Purpose: Wrapper for paging and result-count control widgets.    |
+------------------------------------------------------------------+
-->
<template>
    <div class="control_wrapper">
        <div class="search-controls">
            <div class="custom-select-wrapper">
                <select v-model="localResultsPerPage" @change="onChangeResultsPerPage" class="custom-select">
                    <option v-for="option in resultsPerPageOptions" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
                <img class="custom-caret" src="@/assets/icons/caret-down.svg" />
            </div>
            <span>{{ $t('controlWrapper.resultsShown', { shown: localResultsPerPage, total: totalResults }) }}</span>
        </div>
        <div class="pagination-controls">
            <button @click="prevPage" :disabled="currentPage === 1">{{ $t('controlWrapper.previous') }}</button>
            <button v-if="showFirstEllipsis" @click="goToPage(1)">1</button>
            <span v-if="showFirstEllipsis">...</span>
            <button v-for="page in visiblePages" :key="page" @click="goToPage(page)"
                :class="{ active: currentPage === page }">
                {{ page }}
            </button>
            <span v-if="showLastEllipsis">...</span>
            <button v-if="showLastEllipsis" @click="goToPage(totalPages)">{{ totalPages }}</button>
            <button @click="nextPage" :disabled="currentPage === totalPages">{{ $t('controlWrapper.next') }}</button>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        currentPage: {
            type: Number,
            required: true,
        },
        totalPages: {
            type: Number,
            required: true,
        },
        totalResults: {
            type: Number,
            required: true,
        },
        resultsPerPageOptions: {
            type: Array,
            required: true,
        },
        selectedResultsPerPage: {
            type: Number,
            required: true,
        },
    },
    watch: {
        selectedResultsPerPage(newValue) {
            this.localResultsPerPage = newValue; // Update local value when prop changes
        },
    },
    emits: ['update:selectedResultsPerPage', 'goToPage', 'prevPage', 'nextPage'],
    data() {
        return {
            localResultsPerPage: this.selectedResultsPerPage, // Local copy of the prop
        };
    },
    computed: {
        showFirstEllipsis() {
            return this.currentPage > 3;
        },
        showLastEllipsis() {
            return this.currentPage < this.totalPages - 2;
        },
        visiblePages() {
            const pages = [];
            const startPage = Math.max(this.currentPage - 2, 1);
            const endPage = Math.min(this.currentPage + 2, this.totalPages);
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
            return pages;
        },
    },
    methods: {
        onChangeResultsPerPage() {
            this.$emit('update:selectedResultsPerPage', this.localResultsPerPage);
            this.$emit('changeResultsPerPage', this.localResultsPerPage); // Emit a separate event to fetch new results
        },
        goToPage(page) {
            this.$emit('goToPage', page);
        },
        prevPage() {
            this.$emit('prevPage');
        },
        nextPage() {
            this.$emit('nextPage');
        },
    },
};
</script>

<style scoped>
.control_wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    margin-top: 48px;
}

.search-controls {
    display: flex;
    align-items: center;
    gap: 12px;
}

.custom-select-wrapper {
    position: relative;
    display: inline-block;
    width: 84px;
}

.custom-select {
    width: 100%;
    padding: 12px 16px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-color: var(--brand-bg-1);
    border: 2px solid #EEE;
    border-radius: 4px;
    font-size: 16px;
    color: var(--brand-text-dark);
    cursor: pointer;
    transition: var(--transition-ease);
}

.custom-select:hover,
.custom-select:focus {
    border: 2px solid var(--brand-color-1);
}

.custom-caret {
    position: absolute;
    top: 50%;
    right: 16px;
    pointer-events: none;
    transform: translateY(-50%);
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 2px;
    background: #EEE;
    border: 2px solid #EEE;
    border-radius: 6px;
    overflow: hidden;
    width: fit-content;
}

.pagination-controls button {
    padding: 12px 16px;
    background-color: var(--brand-bg-1);
    border: none;
    cursor: pointer;
    font-weight: 600;
    color: var(--brand-color-1);
}

.pagination-controls button:hover {
    /* background-color: rgba(174, 30, 37, 0.1); */
    background-color: #f0f0f0;
    text-decoration: underline;
}

.pagination-controls button.active {
    background-color: var(--brand-color-1);
    color: var(--brand-text-light)
}

.pagination-controls button:disabled {
    cursor: not-allowed;
    color: #DDD;
    background: var(--brand-bg-1);
}

.pagination-controls span {
    background: var(--brand-bg-1);
    padding: 12px 16px;
}

@media (max-width: 860px) {
    .control_wrapper {
        flex-direction: column;
        align-items: flex-start;
        gap: 24px;
        margin-top: 24px;
    }

    .pagination-controls {
        width: 100%;
    }

    .pagination-controls button {
        flex: 1 0 0;
    }

    .pagination-controls span,
    .pagination-controls button:not(:first-child):not(:last-child) {
        display: none;
    }
}
</style>